import { AuthProvider } from "../../api/dto/auth.dto.js";

export enum LinkedSocialProvider
{
  Google,
  Facebook,
  Apple,
}

export interface LinkedProviderInfo
{
  provider: LinkedSocialProvider;
  email:    string | null;
  name:     string | null;
}

const linked_providers: LinkedProviderInfo[] = [];

export function getLinkedSocialProviders(): LinkedSocialProvider[]
{
  return linked_providers.map( p => p.provider );
}

export function getLinkedProviderInfo( provider: LinkedSocialProvider ): LinkedProviderInfo | undefined
{
  return linked_providers.find( p => p.provider === provider );
}

export function hasLinkedSocialProvider( provider: LinkedSocialProvider ): boolean
{
  return linked_providers.some( p => p.provider === provider );
}

export function addLinkedSocialProvider( provider: LinkedSocialProvider, email: string | null = null, name: string | null = null ): void
{
  if ( hasLinkedSocialProvider( provider ) ) return;
  linked_providers.push( { provider, email, name } );
}

export function removeLinkedSocialProvider( provider: LinkedSocialProvider ): void
{
  const index = linked_providers.findIndex( p => p.provider === provider );
  if ( index !== -1 ) linked_providers.splice( index, 1 );
}

export function resetLinkedSocialProviders(): void
{
  linked_providers.length = 0;
}

const PROVIDER_ID_CACHE_KEYS: Record<string, string> = {
  google:   'web_google_id',
  facebook: 'web_facebook_id',
  apple:    'native_apple_id',
};

export function initLinkedProvidersFromServer( providers: Array<{ provider: string; provider_id?: string | null; email: string | null; name: string | null }> ): void
{
  const map: Record<string, LinkedSocialProvider> = {
    google:   LinkedSocialProvider.Google,
    facebook: LinkedSocialProvider.Facebook,
    apple:    LinkedSocialProvider.Apple,
  };
  for ( const p of providers )
  {
    const provider = map[ p.provider ];
    if ( provider !== undefined ) addLinkedSocialProvider( provider, p.email, p.name );

    if ( p.provider_id )
    {
      const cache_key = PROVIDER_ID_CACHE_KEYS[ p.provider ];
      if ( cache_key ) localStorage.setItem( cache_key, p.provider_id );
    }
  }
}

export function toAuthProvider( provider: LinkedSocialProvider ): AuthProvider
{
  switch ( provider )
  {
    case LinkedSocialProvider.Google:   return AuthProvider.Google;
    case LinkedSocialProvider.Facebook: return AuthProvider.Facebook;
    case LinkedSocialProvider.Apple:    return AuthProvider.Apple;
  }
}

export function fromAuthProvider( provider: string ): LinkedSocialProvider | null
{
  switch ( provider )
  {
    case AuthProvider.Google:   return LinkedSocialProvider.Google;
    case AuthProvider.Facebook: return LinkedSocialProvider.Facebook;
    case AuthProvider.Apple:    return LinkedSocialProvider.Apple;
    default:                    return null;
  }
}

export function providerDisplayName( provider: LinkedSocialProvider ): string
{
  switch ( provider )
  {
    case LinkedSocialProvider.Google:   return "Google";
    case LinkedSocialProvider.Facebook: return "Facebook";
    case LinkedSocialProvider.Apple:    return "Apple";
  }
}
